import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Input, Pagination, Skeleton, Table, Tag, type TableProps } from "antd";
import { Download, FileSpreadsheet, Search } from "lucide-react";

import { Seo } from "@/components/seo/Seo";
import { routePaths } from "@/routes/routePaths";
import { downloadAdminWaitlistExport, getAdminWaitlist } from "@/services/adminApi";
import type { AdminWaitlistSubmission } from "@/types/admin";

function formatDate(value: string): string {
  return value ? new Date(value).toLocaleString() : "-";
}

export function AdminWaitlistPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const [exportError, setExportError] = useState("");

  const waitlistQuery = useQuery({
    queryKey: ["admin", "waitlist", page, pageSize, search],
    queryFn: () => getAdminWaitlist({ page, pageSize, search })
  });

  async function handleExport(format: "csv" | "xlsx") {
    setExportError("");
    setExporting(format);
    try {
      await downloadAdminWaitlistExport(format);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "The waitlist export failed.");
    } finally {
      setExporting(null);
    }
  }

  const columns: TableProps<AdminWaitlistSubmission>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value: string) => value || "-"
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Tier",
      dataIndex: "tier",
      key: "tier",
      render: (value: string) => (
        <Tag color="default" className="m-0 rounded-none">
          {value || "premium"}
        </Tag>
      )
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDate
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: formatDate
    },
    {
      title: "Source",
      dataIndex: "sourcePath",
      key: "sourcePath",
      render: (value: string) => value || "-"
    }
  ];

  const data = waitlistQuery.data;

  return (
    <>
      <Seo
        path={routePaths.admin.waitlist}
        title="Premium Waitlist | VideoTranscriptSaver"
        description="Premium waitlist submissions for VideoTranscriptSaver."
        robots="noindex,nofollow"
      />
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Premium waitlist</h1>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Names and emails submitted from the premium tier registration form.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              icon={<Download className="h-4 w-4" />}
              loading={exporting === "csv"}
              onClick={() => handleExport("csv")}
            >
              CSV
            </Button>
            <Button
              icon={<FileSpreadsheet className="h-4 w-4" />}
              loading={exporting === "xlsx"}
              onClick={() => handleExport("xlsx")}
            >
              XLSX
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Input.Search
            allowClear
            enterButton={<Search className="h-4 w-4" />}
            placeholder="Search name or email"
            className="max-w-md"
            onSearch={(value) => {
              setPage(1);
              setSearch(value.trim());
            }}
          />
          <p className="text-sm text-neutral-500">
            {data ? `${data.total.toLocaleString()} submissions` : "Loading submissions"}
          </p>
        </div>

        {waitlistQuery.error ? (
          <Alert type="error" showIcon message={waitlistQuery.error.message} className="rounded-none" />
        ) : null}

        {exportError ? <Alert type="error" showIcon message={exportError} className="rounded-none" /> : null}

        {waitlistQuery.isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : null}

        {data ? (
          <>
            <div className="overflow-x-auto">
              <Table<AdminWaitlistSubmission>
                rowKey="id"
                columns={columns}
                dataSource={data.items}
                pagination={false}
                loading={waitlistQuery.isFetching}
                scroll={{ x: 860 }}
              />
            </div>
            <Pagination
              current={data.page}
              pageSize={data.pageSize}
              total={data.total}
              showSizeChanger
              pageSizeOptions={["10", "25", "50", "100"]}
              onChange={(nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize);
              }}
            />
          </>
        ) : null}
      </div>
    </>
  );
}
